import React, { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import AppShell from "@/components/AppShell";
import { PageContainer, PageHeader } from "@/components/Page";
import { CATEGORIES } from "@/components/Badges";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { X, Upload, Loader2 } from "lucide-react";

const MAX_FILE_BYTES = 5 * 1024 * 1024; // 5 MB per image
const ALLOWED_TYPES = ["image/jpeg", "image/png", "image/gif", "image/webp"];

export default function NewTicket() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ title: "", description: "", category: "Maintenance" });
  // Each item: { id, previewUrl, uploading, name }
  const [attachments, setAttachments] = useState([]);
  const [saving, setSaving] = useState(false);

  const onFiles = async (files) => {
    const arr = Array.from(files || []).slice(0, 5 - attachments.length);
    for (const f of arr) {
      if (f.size > MAX_FILE_BYTES) { toast.error(`${f.name}: too large (max 5MB)`); continue; }
      if (!ALLOWED_TYPES.includes(f.type)) { toast.error(`${f.name}: unsupported type`); continue; }
      const previewUrl = URL.createObjectURL(f);
      const tempKey = Math.random().toString(36).slice(2);
      setAttachments((prev) => [...prev, { tempKey, id: null, previewUrl, uploading: true, name: f.name }]);
      try {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/attachments/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setAttachments((prev) => prev.map((a) => a.tempKey === tempKey ? { ...a, id: data.id, uploading: false } : a));
      } catch (err) {
        toast.error(`${f.name}: ${formatApiError(err)}`);
        setAttachments((prev) => prev.filter((a) => a.tempKey !== tempKey));
        URL.revokeObjectURL(previewUrl);
      }
    }
  };

  const removeAttachment = (tempKey) => {
    setAttachments((prev) => {
      const a = prev.find((x) => x.tempKey === tempKey);
      if (a?.previewUrl) URL.revokeObjectURL(a.previewUrl);
      return prev.filter((x) => x.tempKey !== tempKey);
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (attachments.some((a) => a.uploading)) {
      toast.error("Please wait for uploads to finish");
      return;
    }
    setSaving(true);
    try {
      const attachmentIds = attachments.map((a) => a.id).filter(Boolean);
      await api.post("/tickets", { ...form, attachments: attachmentIds });
      toast.success("Ticket raised");
      // free blob URLs
      attachments.forEach((a) => a.previewUrl && URL.revokeObjectURL(a.previewUrl));
      navigate("/r/tickets");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  return (
    <AppShell>
      <PageContainer>
        <PageHeader eyebrow="Resident" title="Raise a Ticket" description="Report an issue and the estate manager will follow up." />
        <div className="max-w-2xl">
          <form onSubmit={submit} className="space-y-5 bg-white border border-zinc-200 p-5 sm:p-8" data-testid="new-ticket-form">
            <div>
              <Label className="label-eyebrow">Title</Label>
              <Input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="ticket-title-input" className="rounded-none border-zinc-300 focus-visible:ring-0 focus-visible:border-black mt-2 h-11" placeholder="e.g. Leaking kitchen tap" />
            </div>
            <div>
              <Label className="label-eyebrow">Category</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger className="rounded-none border-zinc-300 mt-2 h-11" data-testid="ticket-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="label-eyebrow">Description</Label>
              <Textarea required rows={6} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="ticket-description-input" className="rounded-none border-zinc-300 focus-visible:ring-0 focus-visible:border-black mt-2" placeholder="Describe the issue in detail…" />
            </div>
            <div>
              <Label className="label-eyebrow">Attach Photos (up to 5, 5MB each)</Label>
              <label
                htmlFor="file-input"
                className="mt-2 flex items-center justify-center gap-2 h-24 border-2 border-dashed border-zinc-300 cursor-pointer hover:border-black hover:bg-zinc-50 transition-colors"
                data-testid="ticket-attach-dropzone"
              >
                <Upload className="h-4 w-4" />
                <span className="text-xs font-semibold uppercase tracking-wider">Click to upload images</span>
              </label>
              <input id="file-input" type="file" accept="image/jpeg,image/png,image/gif,image/webp" multiple hidden onChange={(e) => { onFiles(e.target.files); e.target.value = ""; }} data-testid="ticket-file-input" />
              {attachments.length > 0 && (
                <div className="grid grid-cols-3 md:grid-cols-5 gap-2 mt-3">
                  {attachments.map((a) => (
                    <div key={a.tempKey} className="relative border border-zinc-200 bg-zinc-50" data-testid={`attach-preview-${a.tempKey}`}>
                      <img src={a.previewUrl} alt={a.name} className="w-full h-24 object-cover" />
                      {a.uploading && (
                        <div className="absolute inset-0 bg-white/80 flex items-center justify-center">
                          <Loader2 className="h-4 w-4 animate-spin" />
                        </div>
                      )}
                      <button
                        type="button"
                        onClick={() => removeAttachment(a.tempKey)}
                        data-testid={`remove-attach-${a.tempKey}`}
                        className="absolute -top-2 -right-2 bg-black text-white p-1 hover:bg-red-600 transition-colors"
                      ><X className="h-3 w-3" /></button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="flex items-center gap-3 pt-2">
              <Button type="submit" disabled={saving || attachments.some((a) => a.uploading)} data-testid="submit-ticket-button" className="rounded-none bg-black hover:bg-zinc-800 h-11 px-6 uppercase tracking-wider text-xs font-semibold">
                {saving ? "Sending…" : "Raise Ticket"}
              </Button>
              <button type="button" onClick={() => navigate(-1)} className="text-xs uppercase tracking-wider font-semibold underline underline-offset-4" data-testid="cancel-ticket-button">Cancel</button>
            </div>
          </form>
        </div>
      </PageContainer>
    </AppShell>
  );
}
